/**
 * Raw Products Service
 *
 * API calls for Raw Products (matières premières) feature.
 */

import { apiClient } from '@/api/client';
import type {
  RawProduct,
  RawProductWithRelations,
  CreateRawProductDto,
  UpdateRawProductDto,
  QueryRawProductsDto,
  QueryRawProductsParams,
  RawProductStats,
  PaginatedResult,
  CategoryTreeNode,
  FlatCategory,
  CreateCategoryDto,
  UpdateCategoryDto,
  MoveCategoryDto,
  CategoryWithDetails,
  Unit,
  UnitsByType,
  RawProductPurchasePricesResponse,
  // API Response types
  ApiSuccess,
  RawProductsListResponse,
  RawProductDetailResponse,
  RawProductCreateResponse,
  RawProductUpdateResponse,
  RawProductStatsResponse,
  RawProductCheckSkuResponse,
  CategoriesTreeResponse,
  CategoriesFlatResponse,
  CategoryDetailResponse,
  CategoryCreateResponse,
  CategoryUpdateResponse,
  CategoryMoveResponse,
  UnitsListResponse,
  UnitsByTypeResponse,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  RawProduct,
  RawProductWithRelations,
  CreateRawProductDto,
  UpdateRawProductDto,
  QueryRawProductsDto,
  RawProductStats,
  PaginatedResult,
  CategoryTreeNode,
  FlatCategory,
  CreateCategoryDto,
  UpdateCategoryDto,
  MoveCategoryDto,
  CategoryWithDetails,
  Unit,
  UnitsByType,
};

// Re-export enums
export { MarketType, MarketTypeLabels } from '@bstock/shared';

export const rawProductsService = {
  /**
   * List raw products with pagination and filters
   */
  async list(params: QueryRawProductsDto): Promise<RawProductsListResponse> {
    // Transform marketTypes array to comma-separated string for query params
    const queryParams: QueryRawProductsParams = {
      ...params,
      marketTypes: params.marketTypes?.join(','),
    };
    const response = await apiClient.get<ApiSuccess<RawProductsListResponse>>('/raw-products', { params: queryParams });
    return response.data.data;
  },

  /**
   * Get single raw product by ID
   */
  async getById(id: string): Promise<RawProductDetailResponse> {
    const response = await apiClient.get<ApiSuccess<RawProductDetailResponse>>(`/raw-products/${id}`);
    return response.data.data;
  },

  /**
   * Create a new raw product
   */
  async create(data: CreateRawProductDto): Promise<RawProductCreateResponse> {
    const response = await apiClient.post<ApiSuccess<RawProductCreateResponse>>('/raw-products', data);
    return response.data.data;
  },

  /**
   * Update a raw product
   */
  async update(id: string, data: UpdateRawProductDto): Promise<RawProductUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<RawProductUpdateResponse>>(`/raw-products/${id}`, data);
    return response.data.data;
  },

  /**
   * Archive (soft delete) a raw product
   */
  async archive(id: string): Promise<void> {
    await apiClient.delete(`/raw-products/${id}`);
  },

  /**
   * Get raw products statistics
   */
  async getStats(): Promise<RawProductStatsResponse> {
    const response = await apiClient.get<ApiSuccess<RawProductStatsResponse>>('/raw-products/stats');
    return response.data.data;
  },

  /**
   * Check SKU availability
   */
  async checkSku(sku: string): Promise<RawProductCheckSkuResponse> {
    const response = await apiClient.get<ApiSuccess<RawProductCheckSkuResponse>>('/raw-products/check-sku', { params: { sku } });
    return response.data.data;
  },

  /**
   * Get purchase prices for a raw product from all suppliers
   */
  async getPurchasePrices(rawProductId: string): Promise<RawProductPurchasePricesResponse> {
    const response = await apiClient.get<ApiSuccess<RawProductPurchasePricesResponse>>(`/raw-products/${rawProductId}/purchase-prices`);
    return response.data.data;
  },
};

export const categoriesService = {
  /**
   * Get categories as tree
   */
  async getTree(): Promise<CategoriesTreeResponse> {
    const response = await apiClient.get<ApiSuccess<CategoriesTreeResponse>>('/raw-product-categories');
    return response.data.data;
  },

  /**
   * Get categories as flat list (for selects)
   */
  async getFlat(): Promise<CategoriesFlatResponse> {
    const response = await apiClient.get<ApiSuccess<CategoriesFlatResponse>>('/raw-product-categories', { params: { flat: true } });
    return response.data.data;
  },

  /**
   * Get single category by ID
   */
  async getById(id: string): Promise<CategoryDetailResponse> {
    const response = await apiClient.get<ApiSuccess<CategoryDetailResponse>>(`/raw-product-categories/${id}`);
    return response.data.data;
  },

  /**
   * Create a new category
   */
  async create(data: CreateCategoryDto): Promise<CategoryCreateResponse> {
    const response = await apiClient.post<ApiSuccess<CategoryCreateResponse>>('/raw-product-categories', data);
    return response.data.data;
  },

  /**
   * Update a category
   */
  async update(id: string, data: UpdateCategoryDto): Promise<CategoryUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<CategoryUpdateResponse>>(`/raw-product-categories/${id}`, data);
    return response.data.data;
  },

  /**
   * Move a category (change parent/position)
   */
  async move(id: string, data: MoveCategoryDto): Promise<CategoryMoveResponse> {
    const response = await apiClient.post<ApiSuccess<CategoryMoveResponse>>(`/raw-product-categories/${id}/move`, data);
    return response.data.data;
  },

  /**
   * Archive (soft delete) a category
   */
  async archive(id: string): Promise<void> {
    await apiClient.delete(`/raw-product-categories/${id}`);
  },
};

export const unitsService = {
  /**
   * Get all units
   */
  async list(): Promise<UnitsListResponse> {
    const response = await apiClient.get<ApiSuccess<UnitsListResponse>>('/units');
    return response.data.data;
  },

  /**
   * Get units grouped by type
   */
  async getByType(): Promise<UnitsByTypeResponse> {
    const response = await apiClient.get<ApiSuccess<UnitsByTypeResponse>>('/units', { params: { groupByType: true } });
    return response.data.data;
  },
};
